import React, { useEffect, useRef, useState, useCallback } from "react";
import { gsap } from "gsap";

import slider01 from "../assets/content/main/FADEC.png";
import slider02 from "../assets/content/main/TELEMETRY.png";
import slider03 from "../assets/content/main/Timing.png";
import slider04 from "../assets/content/main/Custom'-is-selected.png";

const products = [
  {
    img: slider01,
    tag: "FADEC",
    title: "Full Authority Digital Engine Control",
    desc: "One of our leading power control systems enabling efficient management of flight engines and their critical components.",
    href: "/space",
  },
  {
    img: slider02,
    tag: "TELEMETRY",
    title: "Integrated Telemetry Systems",
    desc: "High-performance on-board and ground solutions designed for mission-critical missile and aircraft flight tests.",
    href: "/land",
  },
  {
    img: slider03,
    tag: "TIMING",
    title: "Timing & Synchronization",
    desc: "Deterministic timing with nanosecond-level precision across systems.",
    href: "/land",
  },
  {
    img: slider04,
    tag: "CUSTOM",
    title: "Custom Engineering",
    desc: "Application-specific systems designed for complex operational requirements.",
    href: "/land",
  },
];

const ProductSection = () => {
   const [active, setActive] = useState(0);
  const activeRef = useRef(0);
 
  const sectionRef  = useRef(null);
  const handleRef   = useRef(null);
  const progressRef = useRef(null);
  const contentRefs = useRef([]);
  const imageRefs   = useRef([]);
  const tabRefs     = useRef([]);
 
  const animating = useRef(false);
 
  // ─── SLIDE TRANSITION ────────────────────────────────────────────────────
  const goTo = useCallback((index) => {
    const prev = activeRef.current;
 
    if (index === prev) return;
    if (index < 0 || index >= products.length) return;
    if (animating.current) return;
 
    animating.current = true;
 
    // OUT – previous slide
    gsap.to(contentRefs.current[prev], {
      autoAlpha: 0,
      y: 60,
      duration: 0.4,
      ease: "power3.in",
    });
    gsap.to(imageRefs.current[prev], {
      autoAlpha: 0,
      scale: 1.08,
      duration: 0.6,
      ease: "power3.inOut",
    });
 
    // IN – next slide
    gsap.fromTo(
      contentRefs.current[index],
      { autoAlpha: 0, y: 80 },
      { autoAlpha: 1, y: 0, duration: 0.9, delay: 0.15, ease: "power4.out" }
    );
    gsap.fromTo(
      imageRefs.current[index],
      { autoAlpha: 0, scale: 1.15 },
      { autoAlpha: 1, scale: 1, duration: 1.1, ease: "power4.out" }
    );
 
    // Handle pill
    gsap.to(handleRef.current, {
      x: `${index * 120}px`,      // ← adjust 120 to match your handle track width
      duration: 0.75,
      ease: "power3.inOut",
    });
 
    // Progress bar
    gsap.to(progressRef.current, {
      scaleX: (index + 1) / products.length,
      duration: 0.75,
      ease: "power3.inOut",
    });
 
    // Tab active class
    tabRefs.current.forEach((el, i) =>
      el?.classList.toggle("is-active", i === index)
    );
 
    activeRef.current = index;
    setActive(index);
 
    gsap.delayedCall(1, () => {
      animating.current = false;
    });
  }, []);
 
  // ─── INIT + SCROLL / TOUCH HIJACK ────────────────────────────────────────
  useEffect(() => {
    // Set initial GSAP states (run ONCE here – never inside goTo)
    gsap.set(contentRefs.current[0], { autoAlpha: 1, y: 0 });
    gsap.set(contentRefs.current.slice(1), { autoAlpha: 0, y: 80 });
 
    gsap.set(imageRefs.current[0], { autoAlpha: 1, scale: 1 });
    gsap.set(imageRefs.current.slice(1), { autoAlpha: 0, scale: 1.15 });
 
    gsap.set(progressRef.current, {
      scaleX: 1 / products.length,
      transformOrigin: "left center",
    });
 
    const section = sectionRef.current;
 
    const isInView = () => {
      const rect = section.getBoundingClientRect();
      return rect.top <= 10 && rect.bottom >= window.innerHeight - 10;
    };
 
    // WHEEL
    let wheelTimer = null;
    const onWheel = (e) => {
      if (!isInView()) return;
 
      const curr      = activeRef.current;
      const goingDown = e.deltaY > 0;
      const goingUp   = e.deltaY < 0;
 
      // Let page scroll when at boundaries
      if (goingDown && curr === products.length - 1) return;
      if (goingUp   && curr === 0)                   return;
 
      e.preventDefault();
 
      if (wheelTimer) return;
      wheelTimer = setTimeout(() => { wheelTimer = null; }, 900);
 
      goTo(goingDown ? curr + 1 : curr - 1);
    };
 
    // TOUCH
    let touchStartY = 0;
    const onTouchStart = (e) => { touchStartY = e.touches[0].clientY; };
    const onTouchEnd   = (e) => {
      if (!isInView()) return;
 
      const diff      = touchStartY - e.changedTouches[0].clientY;
      if (Math.abs(diff) < 40) return;
 
      const curr      = activeRef.current;
      const goingDown = diff > 0;
      const goingUp   = diff < 0;
 
      if (goingDown && curr === products.length - 1) return;
      if (goingUp   && curr === 0)                   return;
 
      e.preventDefault();
      goTo(goingDown ? curr + 1 : curr - 1);
    };
 
    window.addEventListener("wheel",      onWheel,      { passive: false });
    window.addEventListener("touchstart", onTouchStart, { passive: true  });
    window.addEventListener("touchend",   onTouchEnd,   { passive: false });
 
    return () => {
      window.removeEventListener("wheel",      onWheel);
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchend",   onTouchEnd);
    };
  }, [goTo]);

  return (
    <>
    <div
        style={{
          position: "relative",
          height: `${products.length * 100}vh`,
        }}
      >
        <section
          className="section section--products"
          id="products"
          ref={sectionRef}
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
          }}
        >
          {/* PROGRESS BAR */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "3px",
              background: "rgba(255,255,255,0.15)",
              zIndex: 10,
            }}
          >
            <div
              ref={progressRef}
              style={{
                height: "100%",
                background: "white",
                transformOrigin: "left center",
                transform: "scaleX(0)",
              }}
            />
          </div>
 
          {/* SLIDE COUNTER */}
          <div
            style={{
              position: "absolute",
              bottom: "24px",
              right: "40px",
              zIndex: 10,
              fontVariantNumeric: "tabular-nums",
              fontSize: "0.85rem",
              opacity: 0.5,
              color: "white",
            }}
          >
            {String(active + 1).padStart(2, "0")} /{" "}
            {String(products.length).padStart(2, "0")}
          </div>
 
          {/* VISUAL */}
          <div className="visual">
            {/* IMAGE SLIDER */}
            <div className="img-wrap">
              {products.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => (imageRefs.current[index] = el)}
                  className="img-item"
                  style={{
                    position: "absolute",
                    inset: 0,
                    visibility: "hidden", // GSAP controls via autoAlpha
                    opacity: 0,
                  }}
                >
                  <img
                    src={item.img}
                    className="img-responsive"
                    alt={item.title}
                  />
                </div>
              ))}
            </div>
 
            <div className="visual__handle" ref={handleRef} />
          </div>
 
          {/* CONTENT */}
          <div className="container t-cont pt-a pb-c1">
            <div className="row align-center">
 
              {/* LEFT – text content */}
              <div className="col-7">
                <div className="content-wrap">
                  {products.map((item, index) => (
                    <div
                      key={index}
                      ref={(el) => (contentRefs.current[index] = el)}
                      className="content"
                    >
                      <div>
                        <h2 className="headline-3-en title trn">
                          {item.title}
                        </h2>
                        <p className="body2 desc trn">{item.desc}</p>
                        <div className="bttn-wrap">
                          <a href={item.href} className="bttn bttn-txt--circle">
                            <span className="txt" data-txt="VIEW MORE">
                              VIEW MORE
                            </span>
                            <span className="icon" />
                          </a>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
 
              {/* RIGHT – tab nav */}
              <div className="col-5 col-offset-2 nav">
                <ul
                  className="tab-list"
                  size="s"
                  color="black"
                  lang="kr"
                  type="button"
                >
                  {products.map((item, index) => (
                    <li
                      key={index}
                      ref={(el) => (tabRefs.current[index] = el)}
                      className={`tab-item ${index === active ? "is-active" : ""}`}
                    >
                      <span
                        className="tab-btn trn"
                        onClick={() => goTo(index)}
                      >
                        {item.tag}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
 
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default ProductSection;